/**
 * MCF-SFE wire framing as defined by the DGI protocol annex SECEF BF ST 1.0
 * (docs/SPECIFICATIONS-TECHNIQUES-DES-SFE-ANNEXE-PROTOCOLES-MCF-SFE-v1.0.pdf).
 *
 * Command (§3.2):    SOH LEN SEQ CMD DATA AMB BCC ETX
 * Response (§3.3.2): SOH LEN SEQ CMD DATA BRK STA AMB BCC ETX, or a single
 * NAK (15h) / SYN (16h) byte (§3.3.1).
 *
 * This module holds the framing, the command codes, the C1h reply parser used
 * by port discovery and the chapter 6 status bytes. Field codecs for every
 * command live in `secef-commands.ts`.
 *
 * @module @efacture/mcf-protocol/secef-frame
 */
import { DEFAULT_SECEF_DIALECT } from "./secef-dialect.js";
import { unescapeSecefText } from "./secef-fields.js";
export const SECEF_SOH = 0x01;
export const SECEF_ETX = 0x03;
export const SECEF_BRK = 0x04;
export const SECEF_AMB = 0x05;
export const SECEF_NAK = 0x15;
export const SECEF_SYN = 0x16;
/** §3.2: LEN and BCC carry fixed offsets. */
export const SECEF_LEN_OFFSET = 0x20;
export const SECEF_BCC_OFFSET = 0x30;
/** §3.2: SEQ and CMD range 20h-FFh. */
export const SECEF_SEQ_MIN = 0x20;
export const SECEF_SEQ_MAX = 0xff;
/** §3.2 / §3.3.2: DATA is 0 to 200 bytes. */
export const SECEF_MAX_DATA_LENGTH = 200;
/** §3.3.2: STA is 6 bytes, each 80h-FFh. */
export const SECEF_STATUS_LENGTH = 6;
/** §4.1 command codes. */
export const SECEF_CMD_MCF_STATUS = 0xc1;
export const SECEF_CMD_SERVER_STATUS = 0xc2;
export const SECEF_CMD_TAXPAYER_INFO = 0x2b;
export const SECEF_CMD_CLIENT_INFO = 0xc3;
export const SECEF_CMD_OPEN_INVOICE = 0xc0;
export const SECEF_CMD_ADD_ITEM = 0x31;
export const SECEF_CMD_SUBTOTAL = 0x33;
export const SECEF_CMD_PAYMENT = 0x35;
export const SECEF_CMD_EXTRA_INFO = 0x36;
export const SECEF_CMD_CLOSE_INVOICE = 0x38;
export const SECEF_COMMAND_CODES = Object.freeze([
    SECEF_CMD_MCF_STATUS,
    SECEF_CMD_SERVER_STATUS,
    SECEF_CMD_TAXPAYER_INFO,
    SECEF_CMD_CLIENT_INFO,
    SECEF_CMD_OPEN_INVOICE,
    SECEF_CMD_ADD_ITEM,
    SECEF_CMD_SUBTOTAL,
    SECEF_CMD_PAYMENT,
    SECEF_CMD_EXTRA_INFO,
    SECEF_CMD_CLOSE_INVOICE,
]);
/** §3.1: the MCF answers within 100 ms; the SFE must wait at least 500 ms. */
export const SECEF_MIN_RESPONSE_WAIT_MS = 500;
/**
 * Response bytes between SOH and BCC that are not DATA:
 * LEN + SEQ + CMD + BRK + STA(6) + AMB.
 */
const RESPONSE_FIXED_BODY = 1 + 1 + 1 + 1 + SECEF_STATUS_LENGTH + 1;
/** Largest response DATA a one-byte LEN can describe (FFh - 20h - 11 = 212). */
export const SECEF_MAX_RESPONSE_DATA_LENGTH = 0xff - SECEF_LEN_OFFSET - RESPONSE_FIXED_BODY;
/** Largest command DATA a one-byte LEN can describe (FFh - 20h - 4 = 219). */
const LEN_BOUND_COMMAND_DATA_LENGTH = 0xff - SECEF_LEN_OFFSET - 4;
export class SecefFrameError extends Error {
    constructor(message) {
        super(message);
        this.name = "SecefFrameError";
    }
}
/** DATA does not fit in one frame. */
export class SecefDataTooLongError extends SecefFrameError {
    length;
    max;
    constructor(length, max) {
        super(`DATA trop long : ${length} octets (${max} max)`);
        this.length = length;
        this.max = max;
        this.name = "SecefDataTooLongError";
    }
}
function assertByteInSeqRange(value, field) {
    if (!Number.isInteger(value) || value < SECEF_SEQ_MIN || value > SECEF_SEQ_MAX) {
        throw new SecefFrameError(`${field} hors plage 20h-FFh : ${value}`);
    }
}
/**
 * BCC encoding (§3.2): a 16-bit sum sent as four ASCII nibbles offset by 30h,
 * most significant first (12ABh is sent as 31h 32h 3Ah 3Bh).
 */
export function encodeSecefBcc(sum) {
    const value = sum & 0xffff;
    return Buffer.from([
        ((value >> 12) & 0x0f) + SECEF_BCC_OFFSET,
        ((value >> 8) & 0x0f) + SECEF_BCC_OFFSET,
        ((value >> 4) & 0x0f) + SECEF_BCC_OFFSET,
        (value & 0x0f) + SECEF_BCC_OFFSET,
    ]);
}
/** Decodes four BCC bytes, or returns null when any byte is outside 30h-3Fh. */
export function decodeSecefBcc(bytes) {
    if (bytes.length !== 4)
        return null;
    let value = 0;
    for (const byte of bytes) {
        if (byte < SECEF_BCC_OFFSET || byte > SECEF_BCC_OFFSET + 0x0f)
            return null;
        value = (value << 4) | (byte - SECEF_BCC_OFFSET);
    }
    return value;
}
function assertDataBytes(data, dialect) {
    for (let i = 0; i < data.length; i++) {
        const byte = data[i];
        if (byte < 0x20 && !dialect.dataControlBytes.includes(byte)) {
            throw new SecefFrameError(`octet de contrôle 0x${hex2(byte)} interdit dans DATA (position ${i})`);
        }
    }
}
function sumBytes(bytes, start, endExclusive) {
    let sum = 0;
    for (let i = start; i < endExclusive; i++) {
        sum += bytes[i] ?? 0;
    }
    return sum & 0xffff;
}
/**
 * Builds a command frame (§3.2). LEN counts LEN..AMB plus 20h; BCC sums
 * LEN..AMB (positions 2 to 6 of the command table).
 */
export function buildSecefCommandFrame(seq, cmd, data = Buffer.alloc(0), dialect = DEFAULT_SECEF_DIALECT) {
    assertByteInSeqRange(seq, "SEQ");
    assertByteInSeqRange(cmd, "CMD");
    const max = Math.min(dialect.maxCommandDataLength, LEN_BOUND_COMMAND_DATA_LENGTH);
    if (data.length > max) {
        throw new SecefDataTooLongError(data.length, max);
    }
    assertDataBytes(data, dialect);
    const body = Buffer.concat([
        Buffer.from([SECEF_LEN_OFFSET + 1 + 1 + 1 + data.length + 1, seq, cmd]),
        data,
        Buffer.from([SECEF_AMB]),
    ]);
    return Buffer.concat([
        Buffer.from([SECEF_SOH]),
        body,
        encodeSecefBcc(sumBytes(body, 0, body.length)),
        Buffer.from([SECEF_ETX]),
    ]);
}
/** The C1h MCF status request (§4.4): no data. */
export function buildMcfStatusRequest(seq) {
    return buildSecefCommandFrame(seq, SECEF_CMD_MCF_STATUS);
}
/**
 * Builds a response frame (§3.3.2), with BCC over the span the dialect names.
 * Used by the wire-level simulator and by tests; the SFE never sends one.
 */
export function buildSecefResponseFrame(seq, cmd, data, status, dialect = DEFAULT_SECEF_DIALECT) {
    assertByteInSeqRange(seq, "SEQ");
    assertByteInSeqRange(cmd, "CMD");
    const max = Math.min(dialect.maxResponseDataLength, SECEF_MAX_RESPONSE_DATA_LENGTH);
    if (data.length > max) {
        throw new SecefDataTooLongError(data.length, max);
    }
    assertDataBytes(data, dialect);
    if (status.length !== SECEF_STATUS_LENGTH || status.some((b) => b < 0x80)) {
        throw new SecefFrameError("STA doit compter 6 octets de 80h à FFh");
    }
    const body = Buffer.concat([
        Buffer.from([SECEF_LEN_OFFSET + RESPONSE_FIXED_BODY + data.length, seq, cmd]),
        data,
        Buffer.from([SECEF_BRK]),
        status,
        Buffer.from([SECEF_AMB]),
    ]);
    const bccEnd = dialect.responseBccSpan === "LEN-AMB" ? body.length : 3 + data.length + 1;
    return Buffer.concat([
        Buffer.from([SECEF_SOH]),
        body,
        encodeSecefBcc(sumBytes(body, 0, bccEnd)),
        Buffer.from([SECEF_ETX]),
    ]);
}
/**
 * Reads one MCF reply from the start of `buffer` (§3.3). The frame size is
 * taken from LEN, then every fixed byte, the STA range and the BCC are
 * checked. The BCC is accepted over LEN..AMB or LEN..BRK and the matching
 * span is reported. Call again with more bytes while the result is
 * `incomplete`.
 */
export function readSecefResponse(buffer) {
    let offset = 0;
    while (offset < buffer.length && buffer[offset] === SECEF_SYN) {
        offset++;
    }
    const synCount = offset;
    if (offset >= buffer.length) {
        return { kind: "incomplete", synCount };
    }
    const first = buffer[offset];
    if (first === SECEF_NAK) {
        return { kind: "nak", synCount };
    }
    if (first !== SECEF_SOH) {
        return { kind: "malformed", reason: `octet de début inattendu 0x${hex2(first)} (SOH 01h attendu)`, synCount };
    }
    if (buffer.length < offset + 2) {
        return { kind: "incomplete", synCount };
    }
    const lenByte = buffer[offset + 1];
    const bodyLength = lenByte - SECEF_LEN_OFFSET;
    if (bodyLength < RESPONSE_FIXED_BODY) {
        return { kind: "malformed", reason: `LEN 0x${hex2(lenByte)} trop petit pour une réponse`, synCount };
    }
    const dataLength = bodyLength - RESPONSE_FIXED_BODY;
    const total = 1 + bodyLength + 4 + 1;
    if (buffer.length < offset + total) {
        return { kind: "incomplete", synCount };
    }
    const frame = buffer.subarray(offset, offset + total);
    const brkIndex = 4 + dataLength;
    const staIndex = brkIndex + 1;
    const ambIndex = staIndex + SECEF_STATUS_LENGTH;
    const bccIndex = ambIndex + 1;
    const etxIndex = bccIndex + 4;
    if (frame[brkIndex] !== SECEF_BRK) {
        return { kind: "malformed", reason: `BRK 04h absent (0x${hex2(frame[brkIndex] ?? 0)})`, synCount };
    }
    const status = frame.subarray(staIndex, ambIndex);
    if (status.some((b) => b < 0x80)) {
        return { kind: "malformed", reason: "STA hors plage 80h-FFh", synCount };
    }
    if (frame[ambIndex] !== SECEF_AMB) {
        return { kind: "malformed", reason: `AMB 05h absent (0x${hex2(frame[ambIndex] ?? 0)})`, synCount };
    }
    if (frame[etxIndex] !== SECEF_ETX) {
        return { kind: "malformed", reason: `ETX 03h absent (0x${hex2(frame[etxIndex] ?? 0)})`, synCount };
    }
    const bcc = decodeSecefBcc(frame.subarray(bccIndex, etxIndex));
    if (bcc === null) {
        return { kind: "malformed", reason: "BCC hors plage 30h-3Fh", synCount };
    }
    let bccSpan;
    if (bcc === sumBytes(frame, 1, bccIndex)) {
        bccSpan = "LEN-AMB";
    }
    else if (bcc === sumBytes(frame, 1, staIndex)) {
        bccSpan = "LEN-BRK";
    }
    else {
        return { kind: "malformed", reason: "somme de contrôle (BCC) incorrecte", synCount };
    }
    return {
        kind: "frame",
        synCount,
        frame: {
            seq: frame[2],
            cmd: frame[3],
            data: Buffer.from(frame.subarray(4, brkIndex)),
            status: Buffer.from(status),
            bccSpan,
            consumed: offset + total,
        },
    };
}
/**
 * Reads one command frame (§3.2) from the start of `buffer`: the MCF side of
 * the link. Used by the wire-level simulator.
 */
export function readSecefCommand(buffer) {
    if (buffer.length === 0)
        return { kind: "incomplete" };
    if (buffer[0] !== SECEF_SOH) {
        return { kind: "malformed", reason: "SOH 01h attendu", consumed: 1 };
    }
    if (buffer.length < 2)
        return { kind: "incomplete" };
    const bodyLength = buffer[1] - SECEF_LEN_OFFSET;
    if (bodyLength < 4) {
        return { kind: "malformed", reason: "LEN trop petit", consumed: 2 };
    }
    const total = 1 + bodyLength + 4 + 1;
    if (buffer.length < total)
        return { kind: "incomplete" };
    const ambIndex = bodyLength;
    const bccIndex = ambIndex + 1;
    const etxIndex = bccIndex + 4;
    const bcc = decodeSecefBcc(buffer.subarray(bccIndex, etxIndex));
    if (buffer[ambIndex] !== SECEF_AMB ||
        buffer[etxIndex] !== SECEF_ETX ||
        bcc === null ||
        bcc !== sumBytes(buffer, 1, bccIndex)) {
        return { kind: "malformed", reason: "trame de commande invalide", consumed: total };
    }
    return {
        kind: "frame",
        frame: {
            seq: buffer[2],
            cmd: buffer[3],
            data: Buffer.from(buffer.subarray(4, ambIndex)),
            consumed: total,
        },
    };
}
function hex2(value) {
    return value.toString(16).padStart(2, "0").toUpperCase();
}
// =============================================================================
// C1h MCF status response (§4.4)
// =============================================================================
/** Reverses the §4.2 replacements with the default dialect. */
export function unescapeSecefField(value) {
    return unescapeSecefText(value);
}
const TAX_GROUPS = "ABCDEFGHIJKLMNOP".split("");
const PSVB_GROUPS = "ABCD".split("");
const FIXED_STATUS_FIELDS = 11;
/** Number of fields in a complete C1h reply: 11 + 16 tax rates + 4 PSVB rates. */
export const MCF_STATUS_FIELD_COUNT = FIXED_STATUS_FIELDS + TAX_GROUPS.length + PSVB_GROUPS.length;
/**
 * Parses the DATA of a C1h reply. Missing trailing fields are returned as
 * empty strings and `fieldCount` tells how many were sent.
 */
export function parseMcfStatusData(data) {
    const raw = data.toString("utf8");
    const fields = raw.length === 0 ? [] : raw.split(",").map(unescapeSecefField);
    const at = (i) => (fields[i] ?? "").trim();
    const machineId = at(0);
    const dash = machineId.lastIndexOf("-");
    const taxRates = {};
    TAX_GROUPS.forEach((group, i) => {
        if (fields.length > FIXED_STATUS_FIELDS + i)
            taxRates[group] = at(FIXED_STATUS_FIELDS + i);
    });
    const psvbRates = {};
    PSVB_GROUPS.forEach((group, i) => {
        const index = FIXED_STATUS_FIELDS + TAX_GROUPS.length + i;
        if (fields.length > index)
            psvbRates[group] = at(index);
    });
    return {
        machineId,
        nim: dash > 0 ? machineId.slice(0, dash) : null,
        activationCount: dash > 0 ? machineId.slice(dash + 1) : null,
        ifu: at(1),
        dateTime: at(2),
        totalCounter: at(3),
        salesInvoiceCounter: at(4),
        creditNoteCounter: at(5),
        lastInvoiceDateTime: at(6),
        lastInvoiceType: at(7),
        lastInvoiceSecefCode: at(8),
        lastInvoiceNumber: at(9),
        lastInvoiceAmount: at(10),
        taxRates,
        psvbRates,
        fieldCount: fields.length,
    };
}
const DOCUMENTED_STATUS_BITS = [
    { byte: 0, bit: 5, label: "Erreur code égale à logique OU de toutes les erreurs avec * des octets 0, 1, 2" },
    {
        byte: 0,
        bit: 2,
        label: "La date et l'heure ne sont pas réglées, initialisez la machine et si l'erreur persiste, contactez votre fournisseur",
    },
    { byte: 0, bit: 1, label: "Commande non valide" },
    { byte: 0, bit: 0, label: "Erreur syntaxique dans la commande" },
    { byte: 1, bit: 4, label: "Le rapport quotidien doit être fait" },
    { byte: 1, bit: 2, label: "Mémoire réinitialisée détectée" },
    { byte: 1, bit: 1, label: "Commande interdite dans le mode actuel" },
    { byte: 2, bit: 3, label: "Il y a une facture déjà ouverte" },
    { byte: 4, bit: 4, label: "Mémoire pleine" },
    { byte: 4, bit: 3, label: "Espace disponible pour moins de 50 enregistrements en mémoire" },
    { byte: 4, bit: 2, label: "Enregistrement non-réussite dans la mémoire" },
];
/** Documented bits that report a failed command. */
export const SECEF_STATUS_ERROR_BITS = [
    [0, 0],
    [0, 1],
    [0, 2],
    [1, 1],
    [4, 2],
    [4, 4],
];
/**
 * Bit 1.4: the daily report is due (chapter 6, table 2, p. 24). No command
 * of §4.1 (p. 7) produces a daily report.
 */
export const SECEF_STATUS_DAILY_REPORT_DUE = [1, 4];
/** Documented bits that report a condition without failing the command. */
export const SECEF_STATUS_WARNING_BITS = [
    [1, 2],
    SECEF_STATUS_DAILY_REPORT_DUE,
    [4, 3],
];
/** Bit 2.3: an invoice is open. */
export const SECEF_STATUS_INVOICE_OPEN = [2, 3];
/** Bit 0.5: general error. */
export const SECEF_STATUS_GENERAL_ERROR = [0, 5];
function isBit(flag, bits) {
    return bits.some(([byte, bit]) => flag.byte === byte && flag.bit === bit);
}
export function decodeSecefStatus(status) {
    const flags = DOCUMENTED_STATUS_BITS.filter(({ byte, bit }) => (((status[byte] ?? 0) >> bit) & 1) === 1);
    return {
        hex: status.toString("hex").toUpperCase(),
        flags,
        manufacturerBytes: {
            byte3: hex2(status[3] ?? 0),
            byte5: hex2(status[5] ?? 0),
        },
        errors: flags.filter((f) => isBit(f, [SECEF_STATUS_GENERAL_ERROR, ...SECEF_STATUS_ERROR_BITS])),
        warnings: flags.filter((f) => isBit(f, SECEF_STATUS_WARNING_BITS)),
        invoiceOpen: flags.some((f) => isBit(f, [SECEF_STATUS_INVOICE_OPEN])),
    };
}
/** Builds six STA bytes (each 80h-FFh) with the given `[byte, bit]` pairs set. */
export function buildSecefStatus(bits) {
    const status = Buffer.alloc(SECEF_STATUS_LENGTH, 0x80);
    for (const [byte, bit] of bits) {
        if (byte < 0 || byte >= SECEF_STATUS_LENGTH || bit < 0 || bit > 6) {
            throw new SecefFrameError(`bit d'état ${byte}.${bit} hors plage`);
        }
        status[byte] = status[byte] | (1 << bit);
    }
    return status;
}
